import { useEffect, useState, type ReactNode, Fragment } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { NewItemBadgeIcon } from '../../icons/NewItemBadgeIcon';
import type { ITestSuite, IqeProjectStatus } from '../../interfaces/ui';
import {
  isEnhanceInProgress,
  isExecuteInProgress,
  isGenerateFailed,
  isGenerateOrEnhanceInProgress,
  isProjectOperationInProgress,
  isProjectOperationStopped,
} from '../../utils/projectStatusChecks';
import '../../styles/executionCenter.css';
import '../../styles/projectDetails.css';

interface TestCasesTabProps {
  testSuites: ITestSuite[];
  loading: boolean;
  error: string | null;
  showExecuteButton?: boolean;
  executeButtonDisabled?: boolean;

  actions?: ReactNode;
  isGenerating?: boolean;

  projectStatus: IqeProjectStatus;
  currentRunId?: string;
}

export default function TestCasesTab({
  testSuites,
  loading,
  error,
  actions,
  isGenerating = false,

  projectStatus,
  currentRunId,
}: TestCasesTabProps) {
  const isExecuteInProgressStatus = isExecuteInProgress(projectStatus);
  const isTestPlanUpdateInProgress =
    isGenerateOrEnhanceInProgress(projectStatus);
  const isActiveRunInProgress = isProjectOperationInProgress(projectStatus);
  const hasGenerateFailedStatus = isGenerateFailed(projectStatus);
  const isStopped = isProjectOperationStopped(projectStatus);
  const [expandedSuiteNames, setExpandedSuiteNames] = useState<string[]>([]);
  const hasTestSuites = Boolean(testSuites && testSuites.length > 0);

  const toggleSuite = (suiteName: string) => {
    setExpandedSuiteNames(current =>
      current.includes(suiteName)
        ? current.filter(name => name !== suiteName)
        : [...current, suiteName],
    );
  };

  const isNewTestCase = (testRunId?: string) => {
    const activeRunId = currentRunId?.trim();
    const generatedRunId = testRunId?.trim();
    return Boolean(
      activeRunId && generatedRunId && activeRunId === generatedRunId,
    );
  };

  useEffect(() => {
    if (!currentRunId?.trim() || testSuites.length === 0) return;

    const suitesWithNewTests = testSuites
      .filter(suite => suite.tests.some(test => isNewTestCase(test.runId)))
      .map(suite => suite.name);

    if (suitesWithNewTests.length === 0) return;

    setExpandedSuiteNames(current => {
      const expandedNames = new Set(current);
      const namesToAdd = suitesWithNewTests.filter(
        name => !expandedNames.has(name),
      );
      return namesToAdd.length > 0 ? [...current, ...namesToAdd] : current;
    });
  }, [currentRunId, testSuites]);

  // if (loading && !hasTestSuites) {
  //   return (
  //     <Box className="ec-tab-panel" aria-label="Loading test cases">
  //       <Box className="ec-test-suites-list ec-test-suites-skeleton">
  //         {[0, 1, 2].map(suite => (
  //           <Box
  //             key={suite}
  //             className="ec-test-suite ec-test-suite-skeleton-row"
  //           >
  //             <Skeleton
  //               variant="circle"
  //               className="ec-test-suite-skeleton-toggle"
  //             />
  //             <Skeleton
  //               variant="text"
  //               className="ec-test-suite-skeleton-title"
  //             />
  //           </Box>
  //         ))}
  //       </Box>
  //     </Box>
  //   );
  // }

  const isTestPlanNotAvailable = Boolean(
    error && error.toLowerCase().includes('test plan not available yet'),
  );

  if (error && !hasTestSuites && !isTestPlanNotAvailable) {
    return <Typography className="ec-tab-error">{error}</Typography>;
  }

  if ((loading || isGenerating) && !hasTestSuites) {
    return (
      <Box className="ec-tab-panel">
        <Box className="ec-test-suites-list">
          {[0, 1, 2, 3, 4, 5].map(index => (
            <Box
              key={index}
              className="ec-test-suite ec-test-suite-skeleton-row"
            >
              <Skeleton
                variant="circle"
                className="ec-test-suite-skeleton-toggle"
                animation="wave"
              />
              <Skeleton
                variant="text"
                className="ec-test-suite-skeleton-title"
                animation="wave"
              />
              <Skeleton
                variant="text"
                className="ec-test-suite-skeleton-badge"
                animation="wave"
              />
            </Box>
          ))}
        </Box>
        <Typography className="ec-test-cases-loading-message">
          {isActiveRunInProgress
            ? 'Test cases are being generated and executed in the background. They will appear here once the process is complete.'
            : 'Loading test cases...'}
        </Typography>
      </Box>
    );
  }

  if (!hasTestSuites) {
    return (
      <Box className="pd-empty-state">
        <Box className="pd-empty-icon-box">
          <AssessmentIcon />
        </Box>
        <Typography className="pd-empty-title">No test cases yet</Typography>
        <Typography className="pd-empty-subtitle">
          {isExecuteInProgressStatus
            ? 'Test execution is in progress. Test cases will appear here once available.'
            : hasGenerateFailedStatus
            ? 'Test generation failed. Please retry by clicking the Generate & Execute button.'
            : isStopped
            ? 'This run was stopped. Test cases generated so far have been saved.'
            : 'Test cases will appear here once you run Generate & Execute.'}
        </Typography>
      </Box>
    );
  }

  const totalTestsCount = testSuites.reduce(
    (sum, suite) => sum + suite.tests.length,
    0,
  );
  const totalNewTestsCount = testSuites.reduce(
    (sum, suite) =>
      sum + suite.tests.filter(test => isNewTestCase(test.runId)).length,
    0,
  );

  return (
    <Box className="ec-tab-panel">
      {isTestPlanUpdateInProgress && (
        <Box className="ec-test-cases-info-bar">
          <CircularProgress size={20} className="ec-test-cases-info-spinner" />
          <Typography className="ec-test-cases-info-message">
            {isEnhanceInProgress(projectStatus)
              ? 'Enhancing and executing tests is in progress. The current test cases remain available here, and enhanced test cases will appear after the run completes.'
              : 'Generating and executing tests is in progress. The current test cases remain available here, and refreshed test cases will appear after the run completes.'}
          </Typography>
        </Box>
      )}
      <Box className="ec-test-cases-content-header">
        <Box className="ec-test-cases-title-group">
          <Typography className="ec-artifact-title ec-test-cases-title">
            Test Scenario
          </Typography>
          <Box component="span" className="ec-header-total-count-badge">
            {`${totalTestsCount} ${totalTestsCount === 1 ? 'test' : 'tests'}`}
            {totalNewTestsCount > 0 && (
              <>
                <span className="ec-test-count-separator" aria-hidden="true">
                  •
                </span>
                <span className="ec-test-suite-new-count">
                  {`${totalNewTestsCount} new`}
                </span>
              </>
            )}
          </Box>
        </Box>
        <Box className="ec-test-cases-header-actions">{actions}</Box>
      </Box>
      <>
        {testSuites.length === 0 && (
          <Typography className="ec-empty-suite-message">
            No test suites available for this run.
          </Typography>
        )}
        {testSuites.map(suite => {
          const isExpanded = expandedSuiteNames.includes(suite.name);
          const newTestsCount = suite.tests.filter(test =>
            isNewTestCase(test.runId),
          ).length;
          return (
            <Fragment key={suite.name}>
              <Button
                color="primary"
                className="ec-test-suite-toggle"
                onClick={() => toggleSuite(suite.name)}
                aria-expanded={isExpanded}
                startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                <Box className="ec-test-suite-header-content">
                  <Box className="ec-test-suite-title-group">
                    <Typography className="ec-test-suite-title">
                      {suite.name}
                    </Typography>
                    {newTestsCount > 0 && (
                      <span className="ec-test-suite-new-count">
                        {`${newTestsCount} new`}
                      </span>
                    )}
                  </Box>
                  <Box component="span" className="ec-test-suite-count-badge">
                    {`${suite.tests.length} ${
                      suite.tests.length === 1 ? 'test' : 'tests'
                    }`}
                  </Box>
                </Box>
              </Button>
              {isExpanded && (
                <Box className="ec-test-suite-steps">
                  {suite.tests.length === 0 ? (
                    <Typography className="ec-empty-suite-message">
                      This suite has no tests.
                    </Typography>
                  ) : (
                    suite.tests.map((test, testIndex) => {
                      const isNew = isNewTestCase(test.runId);

                      return (
                        <Box
                          key={`${suite.name}-${test.name}-${testIndex}`}
                          className={`ec-test-case${
                            isNew ? ' ec-test-case-new' : ''
                          }`}
                        >
                          <Typography
                            className="ec-test-case-title"
                            component="div"
                          >
                            <Box className="ec-test-case-title-main">
                              <Box
                                component="span"
                                className="ec-test-case-number-badge"
                              >
                                {testIndex + 1}
                              </Box>
                              <Box
                                component="span"
                                className="ec-test-case-title-text"
                              >
                                {test.title || test.name}
                              </Box>
                            </Box>
                            <Box className="ec-test-case-status-row">
                              {isNew && (
                                <NewItemBadgeIcon className="ec-test-case-new-icon" />
                              )}
                              {test.status && (
                                <>
                                  <Box
                                    component="span"
                                    className={`ec-test-case-status-pill ec-test-case-status-pill-${test.status}`}
                                  >
                                    {test.status === 'automatable'
                                      ? 'Automatable'
                                      : 'Skipped'}
                                  </Box>
                                  {test.status === 'skipped' && (
                                    <Typography
                                      component="span"
                                      className="ec-test-case-reason"
                                    >
                                      - {test.reason || 'Reason not provided.'}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </Typography>

                          {test.preconditions.length > 0 && (
                            <Box className="ec-test-case-details">
                              <Typography
                                variant="caption"
                                className="ec-test-case-section-title"
                              >
                                Preconditions:
                              </Typography>
                              <Box
                                component="ul"
                                className="ec-test-list ec-test-list-tight"
                              >
                                {test.preconditions.map(
                                  (precondition, preIndex) => (
                                    <Typography
                                      key={`${suite.name}-${test.name}-pre-${preIndex}`}
                                      component="li"
                                      variant="body2"
                                      className="ec-test-list-item ec-test-list-item-tight"
                                    >
                                      {precondition}
                                    </Typography>
                                  ),
                                )}
                              </Box>
                            </Box>
                          )}

                          <Box className="ec-test-case-details">
                            <Typography
                              variant="caption"
                              className="ec-test-case-section-title"
                            >
                              Test Steps:
                            </Typography>
                            {test.steps.length === 0 ? (
                              <Typography
                                variant="body2"
                                className="ec-empty-suite-message"
                              >
                                This test has no steps.
                              </Typography>
                            ) : (
                              <Box
                                component="ol"
                                className="ec-test-list ec-test-list-ordered ec-test-list-tight"
                              >
                                {test.steps.map((step, stepIndex) => (
                                  <Typography
                                    key={`${suite.name}-${test.name}-step-${stepIndex}`}
                                    component="li"
                                    variant="body2"
                                    className="ec-test-list-item ec-test-list-item-tight"
                                  >
                                    {step.instruction}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>

                          {test.steps.some(
                            step => step.expectedResults.length > 0,
                          ) && (
                            <Box className="ec-test-case-details">
                              <Typography
                                variant="caption"
                                className="ec-test-case-section-title"
                              >
                                Expected Results:
                              </Typography>
                              <Box
                                component="ul"
                                className="ec-test-list ec-test-list-tight"
                              >
                                {test.steps.flatMap((step, stepIndex) =>
                                  step.expectedResults.map(
                                    (expectedResult, expectedIndex) => (
                                      <Typography
                                        key={`${suite.name}-${test.name}-${stepIndex}-expected-${expectedIndex}`}
                                        component="li"
                                        variant="body2"
                                        className="ec-test-list-item ec-test-list-item-tight"
                                      >
                                        {expectedResult}
                                      </Typography>
                                    ),
                                  ),
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}
            </Fragment>
          );
        })}
      </>
    </Box>
  );
}
